import { t } from '@/i18n'
import type { BillComboProduct, BillProduct } from '@/types'
import type { TabModifier } from '@/types/tabProduct'

import { formatCurrency } from '../fomatter.utils'
import { BODY_FONT, splitLines, wrapText } from '../text.utils'
import type { TicketElement } from '../TicketElement'

export class Product implements TicketElement {
  product: BillProduct
  pickupType: string | undefined
  quantityWidth: number
  priceWidth: number
  lineHeight: number
  paddingBottom: number

  constructor(product: BillProduct, pickupType?: string) {
    this.product = product
    this.pickupType = pickupType
    this.quantityWidth = 35
    this.priceWidth = 50
    this.lineHeight = 22
    this.paddingBottom = 10
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    const infoWidth = width - this.quantityWidth - this.priceWidth
    const nameLines = splitLines(ctx, this.product.name, infoWidth).length
    const modifierLines = this.modifierLines()
      .map((modifier: string) => splitLines(ctx, modifier, infoWidth).length)
      .reduce((total: number, lines: number) => (total += lines), 0)
    const comboProductLines = this.comboProductLines()
      .map((comboProduct: string) => splitLines(ctx, comboProduct, infoWidth).length)
      .reduce((total: number, lines: number) => (total += lines), 0)
    let commentsLines = 0
    if (this.pickupType) {
      commentsLines = splitLines(ctx, this.product.comments, infoWidth).length
    }
    const discountLines = this.product.discountType ? 1 : 0
    return (
      (nameLines + modifierLines + discountLines + comboProductLines + commentsLines) *
        this.lineHeight +
      this.paddingBottom
    )
  }

  modifierLines(modifiers?: TabModifier[], prefix = ''): string[] {
    return (modifiers || this.product.modifiers).map(modifier => {
      let modifierLine = prefix + '+ ' + modifier.name
      if (modifier.quantity! > 1) {
        modifierLine += ' x ' + modifier.quantity
      }
      return modifierLine
    })
  }

  comboProductLines(): string[] {
    return (this.product.comboProducts || []).flatMap((comboProduct: BillComboProduct) => {
      const prefix = '  '
      let comboProductLine = prefix + comboProduct.name
      if (comboProduct.quantity > 1) {
        comboProductLine += ' x ' + comboProduct.quantity
      }
      return [comboProductLine, ...this.modifierLines(comboProduct.modifiers, prefix)]
    })
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    const paddingTop = 20
    top += paddingTop
    ctx.font = `bold 16px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.fillText(this.product.quantity + 'x', 0, top)
    ctx.textAlign = 'right'
    if (this.product.discountType) {
      ctx.font = `16px ${BODY_FONT}`
      const formattedPrice = formatCurrency(this.product.originalPrice)
      ctx.fillText(formattedPrice, width, top)
      const size = ctx.measureText(formattedPrice)
      ctx.fillRect(width - size.width, top - this.lineHeight / 2, size.width, 2)
      ctx.font = `bold 16px ${BODY_FONT}`
      ctx.fillText(formatCurrency(this.product.price), width, top + this.lineHeight)
    } else {
      ctx.fillText(formatCurrency(this.product.price), width, top)
    }
    ctx.font = `16px ${BODY_FONT}`
    ctx.textAlign = 'left'
    const infoWidth = width - this.quantityWidth - this.priceWidth
    top += wrapText(ctx, this.product.name, this.quantityWidth, top, infoWidth) * this.lineHeight
    if (this.product.discountType) {
      ctx.font = `bold 16px ${BODY_FONT}`
      let discountLabel = t('bill.discount')
      if (this.product.discountType === '2x1') {
        discountLabel += ' 2x1'
      } else if (this.product.discountType === 'percentage') {
        const amount = formatCurrency(this.product.originalPrice - this.product.price)
        discountLabel += ` ${this.product.discountAmount}% (-${amount})`
      } else {
        discountLabel += ' ' + formatCurrency(this.product.discountAmount!)
      }
      top += wrapText(ctx, discountLabel, this.quantityWidth, top, infoWidth) * this.lineHeight
    }
    ctx.font = `16px ${BODY_FONT}`
    this.modifierLines().forEach((modifier: string) => {
      top += wrapText(ctx, modifier, this.quantityWidth, top, infoWidth) * this.lineHeight
    })
    this.comboProductLines().forEach((comboProduct: string) => {
      top += wrapText(ctx, comboProduct, this.quantityWidth, top, infoWidth) * this.lineHeight
    })
    if (!!this.pickupType && this.product.comments) {
      top +=
        wrapText(ctx, this.product.comments, this.quantityWidth, top, infoWidth) * this.lineHeight
    }
  }
}
